import { memo, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, Typography } from '@mui/material'
import { blueGrey } from '@mui/material/colors'
import Grid from '@mui/material/Grid'

import { useGetOrderItemsQuery } from '@/app/services/orderItems'
import { useGetOrderQuery } from '@/app/services/orders'
import { setSelectedOrderPaymentStatus, setSelectedOrderStatus } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { useAssignOrderToUser } from '@/common/hooks/useAssignOrderToUser'
import { useOrderNavigation } from '@/common/hooks/useOrderNavigation'
import { ORDER_STATUS_ARRAY } from '@/common/utils/Constants/Constants'

import OrderHeader from './components/OrderHeader/OrderHeader'
import OrderItemList from './components/OrderItemList/OrderItemList'
import OrderItemListActions from './components/OrderItemList/OrderItemListActions'
import OrderSummary from './components/OrderSummary/OrderSummary'
import OrderToolbar from './components/OrderToolbar/OrderToolbar'

const classes = {
    section: {
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: `1px solid ${blueGrey[200]}`,
        width: '100%',
    },
    content: {
        backgroundColor: 'background.paper',
        borderRadius: 2,
        border: `1px solid ${blueGrey[200]}`,
        width: '100%',
        padding: 3,
    },
    empty: {
        paddingBlock: 14,
        height: 'calc(100vh - 385px)',
    },
}

const Order = () => {
    const { t } = useTranslation()
    const { orderId } = useParams()
    const dispatch = useDispatch()
    const navigation = useOrderNavigation('order')

    const organisationId = useSelector(selectOrganisationId)

    const {
        data: order,
        isLoading,
        isSuccess,
    } = useGetOrderQuery({
        organisationId,
        orderId,
    })

    useAssignOrderToUser(order, true)

    const { data: orderItems, isLoading: isLoadingItems } = useGetOrderItemsQuery({
        organisationId,
        orderId,
    })

    const isEditable = useMemo(() => {
        return [ORDER_STATUS_ARRAY.NotCalculated, ORDER_STATUS_ARRAY.Calculated].includes(order?.status)
    }, [order?.status])

    useEffect(() => {
        if (order) {
            dispatch(setSelectedOrderPaymentStatus(order?.paymentStatus))
            dispatch(setSelectedOrderStatus(order?.status))
        }
    }, [order])

    if (navigation) return navigation

    if (!isLoading && isSuccess && isEditable) {
        return (
            <>
                <OrderToolbar key={`${order?.id}-toolbar`} />
                <Grid
                    alignContent="flex-start"
                    component="section"
                    size={{
                        lg: 9,
                        xs: 12,
                    }}
                    spacing={3}
                    container
                >
                    <Grid size={12}>
                        <OrderHeader key={`${order?.id}-header`} />
                        {isLoadingItems ? (
                            <Box
                                sx={[
                                    {
                                        alignItems: 'center',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                    },
                                    classes.content,
                                    classes.empty,
                                ]}
                            >
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography
                                        component="h2"
                                        variant="h6"
                                    >
                                        {t('Loading order items, please wait...')}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : null}
                    </Grid>
                    <Grid
                        size={12}
                        sx={classes.content}
                    >
                        {!isLoadingItems && !orderItems?.length ? (
                            <Box
                                sx={[
                                    {
                                        alignItems: 'center',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                    },
                                    classes.empty,
                                ]}
                            >
                                <Box
                                    sx={{
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography
                                        component="h2"
                                        variant="h6"
                                    >
                                        {t('No parts in this order')}
                                    </Typography>
                                    <Typography>
                                        {t('Please add your parts, select the material and get a order.')}
                                    </Typography>
                                </Box>
                            </Box>
                        ) : null}

                        {isEditable && !isLoadingItems && Boolean(orderItems?.length) ? (
                            <Box
                                sx={[
                                    {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                    },
                                ]}
                            >
                                <Typography
                                    sx={{
                                        mx: 1.5,
                                        textTransform: 'uppercase',
                                    }}
                                    variant="strong1"
                                >
                                    {t('Parts')}
                                </Typography>

                                <OrderItemListActions key={`${order?.id}-item-actions`} />

                                <OrderItemList key={`${order?.id}-item-list`} />
                            </Box>
                        ) : null}
                    </Grid>
                </Grid>

                <Grid
                    component="aside"
                    size={{
                        lg: 3,
                        xs: 12,
                    }}
                    sx={[classes.section, { position: 'sticky', top: '90px' }]}
                >
                    <OrderSummary key={`${order?.id}-summary`} />
                </Grid>
            </>
        )
    }
}

export default memo(Order)
